import { DOCUMENTS_TYPE } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { type TFunction } from "i18next";
import { z } from "zod";

const LANG_BASE = "vessel-call.port-services";
export const PORT_SERVICE_DOCUMENT_TITLE_MAX_CHARS = 50;
export const PORT_SERVICE_DOCUMENT_SUMMARY_MAX_CHARS = 300;
export const PORT_SERVICE_DOCUMENT_OTHER_SPECIFY_MAX_CHARS = 30;

type PortServicesDocument = {
  name?: string;
  documentType?: string;
  otherSpecifyType?: string;
  issueDate?: Date | string | null;
  expirationDate?: Date | string | null;
  documentSummary?: string;
};

const parseDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const isMissingDate = (value?: Date | string | null): boolean => {
  if (!value) return true;
  if (value instanceof Date) return Number.isNaN(value.getTime());
  if (typeof value === "string") return !value.trim() || Number.isNaN(new Date(value).getTime());
  return true;
};

export const validatePortServicesDocument = (
  attachmentDocuments: PortServicesDocument[] | undefined,
  ctx: z.RefinementCtx,
  t: TFunction,
) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  attachmentDocuments?.forEach((document, index) => {
    if (!document.name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.document-title`),
        }),
        path: ["attachmentDocuments", index, "name"],
      });
    }

    if (document.name && document.name.length > PORT_SERVICE_DOCUMENT_TITLE_MAX_CHARS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.max-length", {
          label: t(`${LANG_BASE}.fields.document-title`),
          max: PORT_SERVICE_DOCUMENT_TITLE_MAX_CHARS,
        }),
        path: ["attachmentDocuments", index, "name"],
      });
    }

    if (!document.documentType?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.document-type`),
        }),
        path: ["attachmentDocuments", index, "documentType"],
      });
    }

    if (
      document.otherSpecifyType &&
      document.otherSpecifyType.length > PORT_SERVICE_DOCUMENT_OTHER_SPECIFY_MAX_CHARS
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.max-length", {
          label: t(`${LANG_BASE}.fields.other-specify`),
          max: PORT_SERVICE_DOCUMENT_OTHER_SPECIFY_MAX_CHARS,
        }),
        path: ["attachmentDocuments", index, "otherSpecifyType"],
      });
    }

    if (
      document.documentType === DOCUMENTS_TYPE.OTHER_SPECIFY &&
      !document.otherSpecifyType?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.other-specify`),
        }),
        path: ["attachmentDocuments", index, "otherSpecifyType"],
      });
    }

    if (
      document.documentSummary &&
      document.documentSummary.length > PORT_SERVICE_DOCUMENT_SUMMARY_MAX_CHARS
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.max-length", {
          label: t(`${LANG_BASE}.fields.document-summary`),
          max: PORT_SERVICE_DOCUMENT_SUMMARY_MAX_CHARS,
        }),
        path: ["attachmentDocuments", index, "documentSummary"],
      });
    }

    if (document.documentType === DOCUMENTS_TYPE.CERTIFICATE) {
      if (isMissingDate(document.expirationDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.fields.expiration-date`),
          }),
          path: ["attachmentDocuments", index, "expirationDate"],
        });
      }
    }

    const issueDate = parseDate(document.issueDate);
    const expirationDate = parseDate(document.expirationDate);

    if (issueDate && issueDate > today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.must-not-be-in-future", {
          label: t(`${LANG_BASE}.fields.issue-date`),
        }),
        path: ["attachmentDocuments", index, "issueDate"],
      });
    }

    if (expirationDate && expirationDate < today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.must-not-be-in-past", {
          label: t(`${LANG_BASE}.fields.expiration-date`),
        }),
        path: ["attachmentDocuments", index, "expirationDate"],
      });
    }

    if (issueDate && expirationDate && issueDate > expirationDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.must-be-after", {
          subject: t(`${LANG_BASE}.fields.expiration-date`),
          object: t(`${LANG_BASE}.fields.issue-date`),
        }),
        path: ["attachmentDocuments", index, "expirationDate"],
      });
    }
  });
};
